import { createElement, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLingui } from '@lingui/react';

import { Button } from '@/components/ui/button';
import { isMeSessionExpired, readLastActivity } from '@/components/me/mePortal';
import { logger } from '@/lib/sentry';

import { useMeSignOut } from './useMeSignOut';

/**
 * Reads the `me-last-activity` marker ONCE, on the first render of the
 * signed-in tree (#1768). True when the previous /me session in this browser
 * went idle for 30 minutes or more (the tab was closed, the phone slept), so
 * the session must end before anything loads.
 *
 * A FRESH marker is left alone, on any path: it may belong to a /me tab that
 * is still open in another tab of this browser. When the sign-out fails
 * (offline), `failed` is set and `retry` tries again.
 */
export function useMeBootSignOut(): { expired: boolean; failed: boolean; retry: () => void } {
  const [expired] = useState(() => isMeSessionExpired(readLastActivity(), Date.now()));
  const [failed, setFailed] = useState(false);
  const { signOut } = useMeSignOut();
  const startedRef = useRef(false);

  const attempt = useCallback(() => {
    signOut('idle').catch((error: unknown) => {
      logger.error('me-portal: boot sign-out failed', { error });
      setFailed(true);
    });
  }, [signOut]);

  useEffect(() => {
    if (!expired || startedRef.current) return;
    startedRef.current = true;
    attempt();
  }, [expired, attempt]);

  const retry = useCallback(() => {
    setFailed(false);
    attempt();
  }, [attempt]);

  return { expired, failed, retry };
}

/**
 * Wraps the signed-in tree OUTSIDE `AuthProvider`/`AccessProvider`: on a stale
 * marker it renders only a spinner and signs out (cache and marker cleared
 * first), so `GET /api/v1/access/me` and every /me query never run. A failed
 * sign-out shows an error with a retry instead of spinning forever; the tree
 * stays closed until the session has ended.
 */
export function MeBootGate({ children }: { children: ReactNode }) {
  const { expired, failed, retry } = useMeBootSignOut();
  const { i18n } = useLingui();

  if (!expired) return children;

  const body = failed
    ? createElement(
        'div',
        { className: 'text-center space-y-4', role: 'alert' },
        createElement(
          'p',
          { className: 'text-muted-foreground' },
          i18n._({
            id: 'mePortal.layout.signOutFailed',
            message: 'Could not sign you out. Check your connection and try again.',
          }),
        ),
        createElement(
          Button,
          { className: 'min-h-11', onClick: retry },
          i18n._({ id: 'mePortal.retry', message: 'Try again' }),
        ),
      )
    : createElement(
        'div',
        { className: 'text-center', role: 'status' },
        createElement('div', {
          className: 'animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4',
        }),
        createElement(
          'p',
          { className: 'text-muted-foreground' },
          i18n._({ id: 'mePortal.layout.signingOut', message: 'Signing you out...' }),
        ),
      );

  return createElement(
    'div',
    { className: 'h-screen w-screen flex items-center justify-center bg-background p-4' },
    body,
  );
}
